import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { toast } from 'sonner';
import { Wallet, Clock, UserX, CalendarOff, Plus, Trash2, Edit, Upload, Search, Heart, AlertTriangle, History, Download, ChevronDown, ChevronRight } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const formatEur = (n) => `\u20AC${Number(n || 0).toFixed(2)}`;
const todayKey = () => new Date().toISOString().slice(0, 7);
const currentSchoolYearMonths = () => {
    // Current salary year: Sep 2026 → Aug 2027 (earlier months belong to Previous Year)
    const now = new Date();
    let startYear = now.getMonth() + 1 >= 9 ? now.getFullYear() : now.getFullYear() - 1;
    if (startYear < 2026) startYear = 2026;
    const months = [];
    for (let m = 9; m <= 12; m++) months.push(`${startYear}-${String(m).padStart(2, '0')}`);
    for (let m = 1; m <= 8; m++) months.push(`${startYear + 1}-${String(m).padStart(2, '0')}`);
    return months;
};
const PREV_YEAR_MONTHS = (() => {
    const out = [];
    for (let m = 9; m <= 12; m++) out.push(`2025-${String(m).padStart(2, '0')}`);
    for (let m = 1; m <= 8; m++) out.push(`2026-${String(m).padStart(2, '0')}`);
    return out;
})();
const monthLabelOf = (m) => new Date(m + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

const YEAR_SPLIT = '2026-09-01';
// Splits rows into collapsed "2025-2026" (dated before 01/09/2026) and open "2026-2027" sections.
const YearGroups = ({ rows, dateOf, render, colSpan, emptyText }) => {
    const [openOld, setOpenOld] = useState(false);
    const old = rows.filter(r => (dateOf(r) || '') < YEAR_SPLIT);
    const cur = rows.filter(r => (dateOf(r) || '') >= YEAR_SPLIT);
    const Header = ({ label, count, open, onClick, testId }) => (
        <TableRow className="bg-[#064E3B]/5 hover:bg-[#064E3B]/10 cursor-pointer" onClick={onClick} data-testid={testId}>
            <TableCell colSpan={colSpan} className="py-2 font-semibold text-[#064E3B]"><span className="inline-flex items-center gap-2">{open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}{label}<Badge variant="outline" className="ml-1">{count}</Badge></span></TableCell>
        </TableRow>
    );
    return (<>
        <Header label="2026-2027" count={cur.length} open onClick={() => {}} testId="year-group-current" />
        {cur.length === 0 && <TableRow><TableCell colSpan={colSpan} className="text-center py-4 text-sm text-[#6B7280]">{emptyText}</TableCell></TableRow>}
        {cur.map(render)}
        {old.length > 0 && <Header label="2025-2026" count={old.length} open={openOld} onClick={() => setOpenOld(o => !o)} testId="year-group-previous" />}
        {openOld && old.map(render)}
    </>);
};

const Salaries = () => {
    const { user } = useAuth();
    const isAdmin = user?.role === 'super_admin';
    const [tab, setTab] = useState(isAdmin ? 'counter' : 'absence');
    const [teachers, setTeachers] = useState([]);
    const [programs, setPrograms] = useState([]);
    const [classes, setClasses] = useState([]);

    useEffect(() => {
        Promise.all([
            axios.get(`${API_URL}/api/teachers`, { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get(`${API_URL}/api/programs`, { withCredentials: true }).catch(() => ({ data: [] })),
            axios.get(`${API_URL}/api/classes`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]).then(([t, p, c]) => { setTeachers(t.data); setPrograms(p.data); setClasses(c.data); });
    }, []);

    const tabs = [
        ...(isAdmin ? [{ id: 'counter', label: 'Hours Counter', icon: Clock }] : []),
        { id: 'prevyear', label: 'Previous Year', icon: History },
        { id: 'absence', label: 'Absences', icon: UserX },
        { id: 'holidays', label: 'Holidays', icon: CalendarOff },
        { id: 'extras', label: 'Extras', icon: Plus },
        { id: 'payments', label: 'Payments', icon: Wallet },
        { id: 'probono', label: 'Pro Bono', icon: Heart },
    ];

    return (
        <DashboardLayout>
            <div className="space-y-6" data-testid="salaries-page">
                <div>
                    <h1 className="text-2xl font-bold text-[#1F2937] font-['Outfit']">Teacher Salaries</h1>
                    <p className="text-[#4B5563]">{isAdmin ? 'Manage hours, absences, holidays, extras, payments and pro bono' : 'View teacher salary records and log absences/extras'}</p>
                </div>
                <div className="flex flex-wrap gap-1 bg-[#F3F4F6] p-1 rounded-lg w-fit">
                    {tabs.map(t => {
                        const Icon = t.icon;
                        return (
                            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
                                className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-colors ${tab === t.id ? 'bg-white text-[#064E3B] shadow-sm' : 'text-[#4B5563] hover:text-[#1F2937]'}`}>
                                <Icon className="h-4 w-4" />{t.label}
                            </button>
                        );
                    })}
                </div>

                {tab === 'counter' && isAdmin && <HoursCounterTab teachers={teachers} />}
                {tab === 'prevyear' && <PrevYearTab teachers={teachers} isAdmin={isAdmin} />}
                {tab === 'absence' && <AbsenceTab teachers={teachers} classes={classes} programs={programs} />}
                {tab === 'holidays' && <HolidaysTab programs={programs} isAdmin={isAdmin} />}
                {tab === 'extras' && <ExtrasTab teachers={teachers} />}
                {tab === 'payments' && <PaymentsTab teachers={teachers} isAdmin={isAdmin} />}
                {tab === 'probono' && <ProBonoTab teachers={teachers} programs={programs} classes={classes} isAdmin={isAdmin} />}
            </div>
        </DashboardLayout>
    );
};

// ---------------- Tab: Previous Year Salary (2025-2026) ----------------
const PrevYearTab = ({ teachers, isAdmin }) => {
    const [teacherId, setTeacherId] = useState('');
    const [month, setMonth] = useState('2025-09');
    const [data, setData] = useState(null);
    const [overview, setOverview] = useState(null);
    const [balances, setBalances] = useState(null);
    const [loading, setLoading] = useState(false);
    const [pdfBusy, setPdfBusy] = useState(false);
    const [uploading, setUploading] = useState(false);
    const fileRef = React.useRef(null);

    const loadBalances = () => axios.get(`${API_URL}/api/teacher-salary/previous-year-balances`, { withCredentials: true }).then(r => setBalances(r.data)).catch(() => {});
    useEffect(() => { loadBalances(); }, []);
    const loadOverview = (tid) => {
        axios.get(`${API_URL}/api/teacher-salary/previous-year/${tid}`, { withCredentials: true })
            .then(r => setOverview(r.data)).catch(() => {});
    };
    const savePaid = async (m, amount) => {
        try {
            await axios.post(`${API_URL}/api/teacher-salary/payments`, { teacher_id: teacherId, month: m, amount }, { withCredentials: true });
            toast.success(`Paid amount for ${monthLabelOf(m)} saved`);
            loadOverview(teacherId); loadBalances();
            if (m === month) axios.get(`${API_URL}/api/teacher-salary/previous-year/${teacherId}?month=${month}`, { withCredentials: true }).then(r => setData(r.data)).catch(() => {});
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    };

    useEffect(() => {
        if (!teacherId) return;
        setLoading(true);
        axios.get(`${API_URL}/api/teacher-salary/previous-year/${teacherId}?month=${month}`, { withCredentials: true })
            .then(r => setData(r.data))
            .catch(e => toast.error(e.response?.data?.detail || 'Failed to load previous year salary'))
            .finally(() => setLoading(false));
    }, [teacherId, month]);

    useEffect(() => { if (teacherId) loadOverview(teacherId); }, [teacherId]);

    const downloadTemplate = async () => {
        try {
            const res = await axios.get(`${API_URL}/api/teacher-salary/previous-year-sessions/template`, { withCredentials: true, responseType: 'blob' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(res.data);
            link.download = 'previous_year_sessions_template.xlsx';
            link.click();
            URL.revokeObjectURL(link.href);
        } catch { toast.error('Failed to download template'); }
    };

    const downloadPdf = async () => {
        if (!teacherId) { toast.error('Pick a teacher first'); return; }
        setPdfBusy(true);
        try {
            const res = await axios.get(`${API_URL}/api/teacher-salary/previous-year/${teacherId}/pdf`, { withCredentials: true, responseType: 'blob' });
            const link = document.createElement('a');
            link.href = URL.createObjectURL(res.data);
            link.download = `salary_2025-2026.pdf`;
            link.click();
            URL.revokeObjectURL(link.href);
        } catch { toast.error('Failed to download PDF'); }
        finally { setPdfBusy(false); }
    };

    const handleUpload = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = '';
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append('file', f);
            const r = await axios.post(`${API_URL}/api/teacher-salary/previous-year-sessions/upload`, fd, { withCredentials: true });
            toast.success(`Uploaded ${r.data.inserted} sessions for ${r.data.teachers} teacher(s)${r.data.errors?.length ? ` — ${r.data.errors.length} row errors` : ''}`);
            if (r.data.errors?.length) {
                console.warn('Upload errors:', r.data.errors);
                toast.warning(r.data.errors.slice(0, 3).join(' · '));
            }
            if (teacherId) { setMonth(m => m); loadOverview(teacherId); const d = await axios.get(`${API_URL}/api/teacher-salary/previous-year/${teacherId}?month=${month}`, { withCredentials: true }); setData(d.data); }
        } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
        finally { setUploading(false); }
    };

    const clearUploaded = async () => {
        if (!teacherId) return;
        if (!window.confirm('Remove the uploaded sessions for this teacher and revert to schedule-computed hours?')) return;
        try {
            const r = await axios.delete(`${API_URL}/api/teacher-salary/previous-year-sessions/${teacherId}`, { withCredentials: true });
            toast.success(`Removed ${r.data.deleted} uploaded sessions`);
            loadOverview(teacherId);
            const d = await axios.get(`${API_URL}/api/teacher-salary/previous-year/${teacherId}?month=${month}`, { withCredentials: true });
            setData(d.data);
        } catch { toast.error('Failed to clear uploaded sessions'); }
    };

    return (
        <div className="space-y-4" data-testid="prev-year-tab">
            {balances && (
                <Card className="card-surface" data-testid="prev-year-all-balances">
                    <CardHeader className="pb-2 flex flex-row flex-wrap items-center justify-between gap-2">
                        <CardTitle className="font-['Outfit'] text-base flex items-center gap-2"><History className="h-5 w-5" />All teachers — {balances.year}</CardTitle>
                        <div className="flex gap-2 text-sm">
                            <Badge variant="outline">Earned {formatEur(balances.totals.earned)}</Badge>
                            <Badge variant="outline">Paid {formatEur(balances.totals.paid)}</Badge>
                            <Badge className={balances.totals.unpaid > 0 ? 'bg-[#EF4444]' : 'bg-[#10B981]'} data-testid="prev-year-total-unpaid">Unpaid {formatEur(balances.totals.unpaid)}</Badge>
                        </div>
                    </CardHeader>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader><TableRow><TableHead>Teacher</TableHead><TableHead className="text-right">Hours</TableHead><TableHead className="text-right">Earned</TableHead><TableHead className="text-right">Paid</TableHead><TableHead className="text-right">Balance</TableHead></TableRow></TableHeader>
                            <TableBody>
                                {balances.teachers.map(r => (
                                    <TableRow key={r.teacher_id} className="cursor-pointer hover:bg-[#FAF9F6]" onClick={() => setTeacherId(r.teacher_id)} data-testid={`prev-year-balance-${r.teacher_id}`}>
                                        <TableCell className="font-medium">{r.teacher_name}</TableCell>
                                        <TableCell className="text-right tabular-nums">{r.hours.toFixed(2)} h</TableCell>
                                        <TableCell className="text-right tabular-nums">{formatEur(r.earned)}</TableCell>
                                        <TableCell className="text-right tabular-nums">{formatEur(r.paid)}</TableCell>
                                        <TableCell className={`text-right tabular-nums font-semibold ${r.balance > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{formatEur(r.balance)}</TableCell>
                                    </TableRow>
                                ))}
                                <TableRow className="bg-[#064E3B]/5 font-semibold"><TableCell>Total</TableCell><TableCell className="text-right tabular-nums">{balances.totals.hours.toFixed(2)} h</TableCell><TableCell className="text-right tabular-nums">{formatEur(balances.totals.earned)}</TableCell><TableCell className="text-right tabular-nums">{formatEur(balances.totals.paid)}</TableCell><TableCell className={`text-right tabular-nums ${balances.totals.balance > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{formatEur(balances.totals.balance)}</TableCell></TableRow>
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            )}
            <Card className="card-surface"><CardContent className="p-4 space-y-3">
                <div className="flex flex-wrap gap-3">
                    <div className="space-y-1 flex-1 min-w-[220px]">
                        <Label>Teacher</Label>
                        <Select value={teacherId} onValueChange={setTeacherId}>
                            <SelectTrigger data-testid="prev-year-teacher-select"><SelectValue placeholder="Pick a teacher" /></SelectTrigger>
                            <SelectContent>
                                {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1 w-56">
                        <Label>Month (2025-2026)</Label>
                        <Select value={month} onValueChange={setMonth}>
                            <SelectTrigger data-testid="prev-year-month-select"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {PREV_YEAR_MONTHS.map(m => <SelectItem key={m} value={m}>{monthLabelOf(m)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="prev-year-template-btn"><Download className="h-4 w-4 mr-1" />Excel Template</Button>
                    <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="prev-year-upload-btn">
                        {uploading ? <span className="animate-pulse">Uploading…</span> : <><Upload className="h-4 w-4 mr-1" />Upload Sessions Excel</>}
                    </Button>
                    <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={handleUpload} data-testid="prev-year-upload-input" />
                    <Button variant="outline" size="sm" onClick={downloadPdf} disabled={pdfBusy || !teacherId} data-testid="prev-year-pdf-btn"><Download className="h-4 w-4 mr-1" />{pdfBusy ? 'Preparing…' : 'Download PDF'}</Button>
                    {overview?.source === 'uploaded' && (
                        <Button variant="outline" size="sm" className="text-[#EF4444] border-[#FECACA]" onClick={clearUploaded} data-testid="prev-year-clear-uploaded-btn"><Trash2 className="h-4 w-4 mr-1" />Clear Uploaded</Button>
                    )}
                    <Button variant="outline" size="sm" data-testid="prev-year-seed-btn" onClick={async () => {
                        try {
                            const r = await axios.post(`${API_URL}/api/teacher-salary/previous-year-seed`, {}, { withCredentials: true });
                            toast.success(`Loaded ${r.data.inserted} assignments${r.data.unmatched?.length ? ` — ${r.data.unmatched.length} unmatched` : ''}`);
                            if (r.data.unmatched?.length) console.warn('Unmatched:', r.data.unmatched);
                            if (teacherId) loadOverview(teacherId);
                        } catch (e) { toast.error(e.response?.data?.detail || 'Seed failed'); }
                    }}>Load 2025-2026 List</Button>
                    {overview?.source === 'uploaded' && <Badge variant="outline" className="self-center text-xs">Using uploaded records</Badge>}
                </div>
            </CardContent></Card>

            {!teacherId ? (
                <div className="text-center py-8 text-[#4B5563]">Pick a teacher to view their Sep 2025 → Aug 2026 salary, exactly like the current year view.</div>
            ) : loading ? (
                <div className="p-10 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#064E3B]" /></div>
            ) : data ? (
                <CounterTables data={data} />
            ) : null}

            {teacherId && overview && (
                <Card className="card-surface">
                    <CardHeader><CardTitle className="font-['Outfit'] flex items-center gap-2"><History className="h-5 w-5" />Year Overview (2025-2026)</CardTitle></CardHeader>
                    <CardContent className="p-0"><PrevYearBreakdown data={overview} onPaidChange={isAdmin ? savePaid : null} /></CardContent>
                </Card>
            )}
        </div>
    );
};

const PaidCell = ({ value, onSave, testId }) => {
    const [v, setV] = useState(String(value ?? 0));
    useEffect(() => { setV(String(value ?? 0)); }, [value]);
    const commit = () => { const n = parseFloat(v); if (isNaN(n) || n < 0) { setV(String(value)); return; } if (n !== Number(value)) onSave(n); };
    return <Input type="number" step="0.01" min="0" value={v} onChange={e => setV(e.target.value)} onBlur={commit} onKeyDown={e => e.key === 'Enter' && e.target.blur()} className="h-8 w-28 ml-auto text-right tabular-nums" data-testid={testId} />;
};

export const PrevYearBreakdown = ({ data, onPaidChange }) => (
    <div>
        <div className="px-4 py-3 flex flex-wrap items-center gap-2 border-b border-[#E5E7EB] bg-[#FAF9F6]">
            <span className="text-sm text-[#4B5563]">Last year classes:</span>
            {data.has_assignments ? data.assignments.map((a, i) => (
                <Badge key={i} variant="outline" className="text-xs">{a.program_short_name || a.program_name} · <span dir="rtl" className="ml-1">{a.class_name}</span></Badge>
            )) : <span className="text-xs italic text-[#B45309]">No previous-year assignments recorded for this teacher</span>}
            <span className="ml-auto text-sm text-[#4B5563]">Hourly rate: <span className="font-semibold tabular-nums">{formatEur(data.hourly_rate)}</span></span>
        </div>
        <Table>
            <TableHeader><TableRow>
                <TableHead>Month</TableHead>
                <TableHead className="text-right">Hours</TableHead>
                <TableHead className="text-right">Earned</TableHead>
                <TableHead className="text-right">Paid</TableHead>
                <TableHead className="text-right">Balance</TableHead>
                <TableHead className="text-right">Notes</TableHead>
            </TableRow></TableHeader>
            <TableBody>
                {data.months.map(m => (
                    <TableRow key={m.month} data-testid={`prev-year-row-${m.month}`}>
                        <TableCell>{m.month_label}</TableCell>
                        <TableCell className="text-right tabular-nums">{m.hours.toFixed(2)} h</TableCell>
                        <TableCell className="text-right tabular-nums">{formatEur(m.earned)}</TableCell>
                        <TableCell className="text-right tabular-nums">{onPaidChange ? <PaidCell value={m.paid} onSave={(n) => onPaidChange(m.month, n)} testId={`prev-year-paid-${m.month}`} /> : formatEur(m.paid)}</TableCell>
                        <TableCell className={`text-right tabular-nums font-medium ${m.balance > 0 ? 'text-[#EF4444]' : ''}`}>{formatEur(m.balance)}</TableCell>
                        <TableCell className="text-right text-xs text-[#6B7280]">
                            {m.pro_bono_sessions > 0 && <Badge variant="outline" className="text-xs mr-1">Pro Bono ×{m.pro_bono_sessions}</Badge>}
                            {m.absent_sessions > 0 && <Badge variant="outline" className="text-xs text-[#EF4444] border-[#FECACA]">Absent ×{m.absent_sessions}</Badge>}
                        </TableCell>
                    </TableRow>
                ))}
                <TableRow className="bg-[#064E3B]/5 font-semibold" data-testid="prev-year-totals">
                    <TableCell>Year Total (2025-2026)</TableCell>
                    <TableCell className="text-right tabular-nums">{data.totals.hours.toFixed(2)} h</TableCell>
                    <TableCell className="text-right tabular-nums">{formatEur(data.totals.earned)}</TableCell>
                    <TableCell className="text-right tabular-nums">{formatEur(data.totals.paid)}</TableCell>
                    <TableCell className={`text-right tabular-nums ${data.totals.balance > 0 ? 'text-[#EF4444]' : 'text-[#10B981]'}`}>{formatEur(data.totals.balance)}</TableCell>
                    <TableCell />
                </TableRow>
            </TableBody>
        </Table>
    </div>
);

// ---------------- Tab 1: Hours Counter ----------------
const HoursCounterTab = ({ teachers }) => {
    const [teacherId, setTeacherId] = useState('');
    const [month, setMonth] = useState(() => { const tk = todayKey(); return tk < '2026-09' ? '2026-09' : tk; });
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!teacherId) return;
        setLoading(true);
        axios.get(`${API_URL}/api/teacher-salary/teacher/${teacherId}?month=${month}`, { withCredentials: true })
            .then(r => setData(r.data))
            .catch(e => toast.error(e.response?.data?.detail || 'Failed'))
            .finally(() => setLoading(false));
    }, [teacherId, month]);

    return (
        <div className="space-y-4">
            <Card className="card-surface"><CardContent className="p-4 flex flex-wrap gap-3">
                <div className="space-y-1 flex-1 min-w-[220px]">
                    <Label>Teacher</Label>
                    <Select value={teacherId} onValueChange={setTeacherId}>
                        <SelectTrigger data-testid="counter-teacher-select"><SelectValue placeholder="Pick a teacher" /></SelectTrigger>
                        <SelectContent>
                            {teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </div>
                <div className="space-y-1 w-56">
                    <Label>Month</Label>
                    <Select value={month} onValueChange={setMonth}>
                        <SelectTrigger data-testid="counter-month-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            {currentSchoolYearMonths().map(m => (
                                <SelectItem key={m} value={m}>{new Date(m + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </CardContent></Card>

            {loading ? <div className="text-center py-8">Loading…</div>
                : data ? <CounterTables data={data} />
                : <div className="text-center py-8 text-[#4B5563]">Select a teacher to view their hours.</div>}
        </div>
    );
};

const CounterTables = ({ data }) => (
    <div className="space-y-4">
        <Card className="card-surface"><CardHeader><CardTitle className="font-['Outfit']">{data.month_label} — {data.teacher_name}</CardTitle></CardHeader>
            <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <Stat label="Hours worked" value={`${data.hours_worked.toFixed(2)} h`} />
                    <Stat label="Hourly rate" value={formatEur(data.hourly_rate)} />
                    <Stat label="Total" value={formatEur(data.total_deserved)} accent />
                    <Stat label="Paid" value={formatEur(data.paid)} />
                    <Stat label="Balance" value={formatEur(data.month_balance)} positive={data.month_balance >= 0} />
                </div>
            </CardContent>
        </Card>
        <Card className="card-surface"><CardHeader><CardTitle className="font-['Outfit']">Hours Details</CardTitle></CardHeader>
            <CardContent className="p-0">
                <Table>
                    <TableHeader><TableRow><TableHead>Date</TableHead><TableHead>Details</TableHead><TableHead>Start</TableHead><TableHead>End</TableHead><TableHead className="text-right">Duration</TableHead></TableRow></TableHeader>
                    <TableBody>
                        {data.sessions.length === 0 ? <TableRow><TableCell colSpan={5} className="text-center py-6 text-[#4B5563]">No sessions yet</TableCell></TableRow>
                        : data.sessions.map(s => {
                            const red = s.is_absent;
                            return <TableRow key={s.id} className={red ? 'bg-[#FEF2F2]' : ''}>
                                <TableCell className={red ? 'text-[#EF4444]' : ''}>{new Date(s.date + 'T00:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}</TableCell>
                                <TableCell className={red ? 'text-[#EF4444]' : ''}>{s.details}{s.note && <span className="ml-2 text-xs font-semibold">{s.note}</span>}</TableCell>
                                <TableCell className={`tabular-nums ${red ? 'text-[#EF4444]' : ''}`}>{s.start_time}</TableCell>
                                <TableCell className={`tabular-nums ${red ? 'text-[#EF4444]' : ''}`}>{s.end_time}</TableCell>
                                <TableCell className={`text-right tabular-nums ${red ? 'text-[#EF4444]' : ''}`}>{(s.duration || 0).toFixed(2)} h</TableCell>
                            </TableRow>;
                        })}
                    </TableBody>
                </Table>
                <div className="flex justify-between items-center px-4 py-3 border-t bg-[#F9FAFB]"><span className="text-sm text-[#4B5563]">Total duration</span><span className="font-semibold tabular-nums">{data.sessions.reduce((s, r) => s + (r.duration || 0), 0).toFixed(2)} h</span></div>
            </CardContent>
        </Card>
        <Card className="card-surface"><CardHeader><CardTitle className="font-['Outfit']">Balance Summary</CardTitle></CardHeader>
            <CardContent><div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Stat label="Previous balance" value={formatEur(data.previous_balance)} positive={data.previous_balance >= 0} />
                <Stat label="This month balance" value={formatEur(data.month_balance)} positive={data.month_balance >= 0} />
                <Stat label="Final balance" value={formatEur(data.final_balance)} positive={data.final_balance >= 0} accent />
            </div></CardContent>
        </Card>
    </div>
);

// ---------------- Tab 2: Absences ----------------
const AbsenceTab = ({ teachers, classes, programs }) => {
    const [rows, setRows] = useState([]);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ teacher_id: '', class_id: '__none__', start_date: '', end_date: '', notes: '' });

    const reload = () => axios.get(`${API_URL}/api/teacher-salary/absences${search ? '?search=' + encodeURIComponent(search) : ''}`, { withCredentials: true }).then(r => setRows(r.data));
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [search]);

    // Programs (used for absence-class labels: show "Program Name — code" instead of bare class codes)
    const programsById = useMemo(() => {
        const map = {};
        (programs || []).forEach(p => { map[p.id] = p; });
        return map;
    }, [programs]);
    const teacherClasses = useMemo(() => classes.filter(c => c.main_teacher_id === form.teacher_id), [classes, form.teacher_id]);
    const classLabel = (c) => {
        const prog = programsById[c.program_id];
        const progName = prog?.name || prog?.short_name || '';
        const cls = c.english_name || c.name || c.code || '';
        return progName ? (cls ? `${progName} — ${cls}` : progName) : cls;
    };

    const submit = async () => {
        try {
            const payload = { ...form, class_id: form.class_id === '__none__' ? null : form.class_id };
            if (editing) await axios.put(`${API_URL}/api/teacher-salary/absences/${editing.id}`, payload, { withCredentials: true });
            else await axios.post(`${API_URL}/api/teacher-salary/absences`, payload, { withCredentials: true });
            toast.success('Saved');
            setOpen(false); setEditing(null); reload();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };
    const del = async (id) => { if (!window.confirm('Delete?')) return; await axios.delete(`${API_URL}/api/teacher-salary/absences/${id}`, { withCredentials: true }); reload(); };

    return (
        <Card className="card-surface">
            <CardHeader className="flex flex-row items-center justify-between gap-3">
                <CardTitle className="font-['Outfit']">Absences</CardTitle>
                <div className="flex gap-2">
                    <div className="relative"><Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#4B5563]" /><Input placeholder="Search teacher / date / program…" value={search} onChange={e => setSearch(e.target.value)} className="pl-9 w-64" /></div>
                    <Button onClick={() => { setEditing(null); setForm({ teacher_id: '', class_id: '__none__', start_date: '', end_date: '', notes: '' }); setOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="add-absence-btn"><Plus className="h-4 w-4 mr-1" />Record Absence</Button>
                </div>
            </CardHeader>
            <CardContent className="p-0">
                <Table>
                    <TableHeader><TableRow><TableHead>Teacher</TableHead><TableHead>Scope</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Notes</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                        <YearGroups rows={rows} dateOf={r => r.start_date} colSpan={6} emptyText="No absences recorded for 2026-2027." render={r => <TableRow key={r.id}>
                            <TableCell>{r.teacher_name}</TableCell>
                            <TableCell>{r.class_id ? <Badge variant="outline">{r.program_name || r.program_short_name}{r.class_label ? ` — ${r.class_label}` : ''}</Badge> : <Badge variant="outline">All classes</Badge>}</TableCell>
                            <TableCell className="tabular-nums">{r.start_date}</TableCell>
                            <TableCell className="tabular-nums">{r.end_date}</TableCell>
                            <TableCell className="text-xs">{r.notes}</TableCell>
                            <TableCell className="text-right">
                                <Button size="sm" variant="outline" onClick={() => { setEditing(r); setForm({ teacher_id: r.teacher_id, class_id: r.class_id || '__none__', start_date: r.start_date, end_date: r.end_date, notes: r.notes || '' }); setOpen(true); }}><Edit className="h-3 w-3" /></Button>
                                <Button size="sm" variant="outline" className="ml-2 text-[#EF4444]" onClick={() => del(r.id)}><Trash2 className="h-3 w-3" /></Button>
                            </TableCell>
                        </TableRow>} />
                    </TableBody>
                </Table>
            </CardContent>

            <Dialog open={open} onOpenChange={setOpen}><DialogContent>
                <DialogHeader><DialogTitle className="font-['Outfit']">{editing ? 'Edit' : 'Record'} Absence</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5"><Label>Teacher</Label>
                        <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v, class_id: '__none__' })}>
                            <SelectTrigger><SelectValue placeholder="Pick teacher" /></SelectTrigger>
                            <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5"><Label>Specific class (optional — leave blank for all classes in date range)</Label>
                        <Select value={form.class_id} onValueChange={(v) => setForm({ ...form, class_id: v })}>
                            <SelectTrigger><SelectValue placeholder="All classes" /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="__none__">All classes</SelectItem>
                                {teacherClasses.map(c => <SelectItem key={c.id} value={c.id}>{classLabel(c)}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label>From</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>To</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
                    </div>
                    <div className="space-y-1.5"><Label>Notes</Label><Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button onClick={submit} className="bg-[#064E3B] hover:bg-[#047857]">Save</Button>
                </DialogFooter>
            </DialogContent></Dialog>
        </Card>
    );
};

// ---------------- Tab 3: Holidays ----------------
const HolidaysTab = ({ programs, isAdmin }) => {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ scope: 'school', program_id: '', branch: '', start_date: '', end_date: '', name: '' });
    const reload = () => axios.get(`${API_URL}/api/teacher-salary/holidays`, { withCredentials: true }).then(r => setRows(r.data));
    useEffect(() => { reload(); }, []);
    const submit = async () => {
        try {
            await axios.post(`${API_URL}/api/teacher-salary/holidays`, form, { withCredentials: true });
            toast.success('Saved'); setOpen(false); reload();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };
    const del = async (id) => { if (!window.confirm('Delete?')) return; await axios.delete(`${API_URL}/api/teacher-salary/holidays/${id}`, { withCredentials: true }); reload(); };
    return (
        <Card className="card-surface">
            <CardHeader className="flex flex-row items-center justify-between"><CardTitle className="font-['Outfit']">Holidays</CardTitle>
                {isAdmin && <Button onClick={() => { setForm({ scope: 'school', program_id: '', branch: '', start_date: '', end_date: '', name: '' }); setOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]"><Plus className="h-4 w-4 mr-1" />Add Holiday</Button>}
            </CardHeader>
            <CardContent className="p-0">
                <Table>
                    <TableHeader><TableRow><TableHead>Scope</TableHead><TableHead>Name</TableHead><TableHead>From</TableHead><TableHead>To</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                    <TableBody>
                        <YearGroups rows={rows} dateOf={r => r.start_date} colSpan={isAdmin ? 5 : 4} emptyText="No holidays recorded for 2026-2027." render={r => <TableRow key={r.id}>
                            <TableCell>{r.scope === 'school' ? <Badge variant="outline">Whole school</Badge> : r.scope === 'branch' ? <Badge variant="outline">Branch: {r.branch}</Badge> : <Badge variant="outline">{r.program_short_name}</Badge>}</TableCell>
                            <TableCell>{r.name}</TableCell>
                            <TableCell className="tabular-nums">{r.start_date}</TableCell>
                            <TableCell className="tabular-nums">{r.end_date}</TableCell>
                            {isAdmin && <TableCell className="text-right"><Button size="sm" variant="outline" className="text-[#EF4444]" onClick={() => del(r.id)}><Trash2 className="h-3 w-3" /></Button></TableCell>}
                        </TableRow>} />
                    </TableBody>
                </Table>
            </CardContent>
            {isAdmin && <Dialog open={open} onOpenChange={setOpen}><DialogContent>
                <DialogHeader><DialogTitle className="font-['Outfit']">Add Holiday</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5"><Label>Scope</Label>
                        <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, program_id: '', branch: '' })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="school">Whole school</SelectItem><SelectItem value="branch">Specific branch</SelectItem><SelectItem value="program">Specific program</SelectItem></SelectContent>
                        </Select>
                    </div>
                    {form.scope === 'branch' && (<div className="space-y-1.5"><Label>Branch</Label>
                        <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                            <SelectTrigger data-testid="holiday-branch-select"><SelectValue placeholder="Pick a branch" /></SelectTrigger>
                            <SelectContent>{Array.from(new Set(programs.map(p => p.location).filter(Boolean))).sort().map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>)}
                    {form.scope === 'program' && (<div className="space-y-1.5"><Label>Program</Label>
                        <Select value={form.program_id} onValueChange={(v) => setForm({ ...form, program_id: v })}>
                            <SelectTrigger><SelectValue placeholder="Pick a program" /></SelectTrigger>
                            <SelectContent>{programs.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>)}
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label>From</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>To</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
                    </div>
                    <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Eid Al-Fitr" /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#064E3B] hover:bg-[#047857]">Save</Button></DialogFooter>
            </DialogContent></Dialog>}
        </Card>
    );
};

// ---------------- Tab 4: Extras ----------------
const ExtrasTab = ({ teachers }) => {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState({ teacher_id: '', date: '', start_time: '', end_time: '', note: '' });
    const reload = () => axios.get(`${API_URL}/api/teacher-salary/extras`, { withCredentials: true }).then(r => setRows(r.data));
    useEffect(() => { reload(); }, []);
    const submit = async () => {
        try {
            if (editing) await axios.put(`${API_URL}/api/teacher-salary/extras/${editing.id}`, form, { withCredentials: true });
            else await axios.post(`${API_URL}/api/teacher-salary/extras`, form, { withCredentials: true });
            toast.success('Saved'); setOpen(false); setEditing(null); reload();
        } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };
    const del = async (id) => { if (!window.confirm('Delete?')) return; await axios.delete(`${API_URL}/api/teacher-salary/extras/${id}`, { withCredentials: true }); reload(); };
    return (
        <Card className="card-surface">
            <CardHeader className="flex flex-row items-center justify-between"><CardTitle className="font-['Outfit']">Extras (Overtime)</CardTitle>
                <Button onClick={() => { setEditing(null); setForm({ teacher_id: '', date: '', start_time: '', end_time: '', note: '' }); setOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]"><Plus className="h-4 w-4 mr-1" />Add Extra</Button>
            </CardHeader>
            <CardContent className="p-0">
                <Table>
                    <TableHeader><TableRow><TableHead>Teacher</TableHead><TableHead>Date</TableHead><TableHead>Start</TableHead><TableHead>End</TableHead><TableHead>Duration</TableHead><TableHead>Note</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
                    <TableBody>
                        <YearGroups rows={rows} dateOf={r => r.date} colSpan={7} emptyText="No extras recorded for 2026-2027." render={r => <TableRow key={r.id}>
                            <TableCell>{r.teacher_name}</TableCell><TableCell className="tabular-nums">{r.date}</TableCell>
                            <TableCell className="tabular-nums">{r.start_time}</TableCell><TableCell className="tabular-nums">{r.end_time}</TableCell>
                            <TableCell className="tabular-nums">{(r.duration || 0).toFixed(2)} h</TableCell><TableCell className="text-xs">{r.note}</TableCell>
                            <TableCell className="text-right">
                                <Button size="sm" variant="outline" onClick={() => { setEditing(r); setForm({ teacher_id: r.teacher_id, date: r.date, start_time: r.start_time || '', end_time: r.end_time || '', note: r.note || '' }); setOpen(true); }}><Edit className="h-3 w-3" /></Button>
                                <Button size="sm" variant="outline" className="ml-2 text-[#EF4444]" onClick={() => del(r.id)}><Trash2 className="h-3 w-3" /></Button>
                            </TableCell>
                        </TableRow>} />
                    </TableBody>
                </Table>
            </CardContent>
            <Dialog open={open} onOpenChange={setOpen}><DialogContent>
                <DialogHeader><DialogTitle className="font-['Outfit']">{editing ? 'Edit' : 'Add'} Extra</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5"><Label>Teacher</Label>
                        <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v })}>
                            <SelectTrigger><SelectValue placeholder="Pick teacher" /></SelectTrigger>
                            <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5"><Label>Date</Label><Input type="date" value={form.date} onChange={e => setForm({ ...form, date: e.target.value })} /></div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label>Start</Label><Input type="time" value={form.start_time} onChange={e => setForm({ ...form, start_time: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>End</Label><Input type="time" value={form.end_time} onChange={e => setForm({ ...form, end_time: e.target.value })} /></div>
                    </div>
                    <div className="space-y-1.5"><Label>Note (shown in hours details)</Label><Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#064E3B] hover:bg-[#047857]">Save</Button></DialogFooter>
            </DialogContent></Dialog>
        </Card>
    );
};

// ---------------- Tab 5: Payments ----------------
const PaymentsTab = ({ teachers, isAdmin }) => {
    const [month, setMonth] = useState(todayKey());
    const [rows, setRows] = useState([]);
    const [drafts, setDrafts] = useState({});       // { teacher_id: amount-as-string }
    const [savingId, setSavingId] = useState(null);
    const [dirtyIds, setDirtyIds] = useState(new Set());

    const reload = () =>
        axios.get(`${API_URL}/api/teacher-salary/payments?month=${month}`, { withCredentials: true })
            .then(r => {
                setRows(r.data);
                const next = {};
                (r.data || []).forEach(x => { next[x.teacher_id] = String(x.amount ?? 0); });
                setDrafts(next);
                setDirtyIds(new Set());
            });
    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [month]);

    const saveOne = async (teacher_id) => {
        const raw = drafts[teacher_id];
        const amount = parseFloat(raw);
        if (Number.isNaN(amount)) {
            toast.error('Enter a valid number');
            return;
        }
        // Compare against last saved value
        const saved = rows.find(r => r.teacher_id === teacher_id)?.amount ?? 0;
        if (Number(saved) === amount) {
            setDirtyIds(prev => { const n = new Set(prev); n.delete(teacher_id); return n; });
            return; // nothing changed
        }
        setSavingId(teacher_id);
        try {
            await axios.post(`${API_URL}/api/teacher-salary/payments`,
                { teacher_id, month, amount }, { withCredentials: true });
            // Update local snapshot so we know what "saved" is
            setRows(prev => prev.map(r => r.teacher_id === teacher_id ? { ...r, amount } : r));
            setDirtyIds(prev => { const n = new Set(prev); n.delete(teacher_id); return n; });
            toast.success('Saved');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to save');
        } finally {
            setSavingId(null);
        }
    };

    const saveAllDirty = async () => {
        for (const tid of Array.from(dirtyIds)) {
            // eslint-disable-next-line no-await-in-loop
            await saveOne(tid);
        }
    };

    const onChange = (teacher_id, value) => {
        setDrafts(prev => ({ ...prev, [teacher_id]: value }));
        const saved = rows.find(r => r.teacher_id === teacher_id)?.amount ?? 0;
        const changed = parseFloat(value || '0') !== Number(saved);
        setDirtyIds(prev => {
            const n = new Set(prev);
            if (changed) n.add(teacher_id); else n.delete(teacher_id);
            return n;
        });
    };

    const upload = async (e) => {
        const file = e.target.files?.[0]; if (!file) return;
        // Warn if there are unsaved edits before the upload overwrites them
        if (dirtyIds.size > 0 && !window.confirm(`You have ${dirtyIds.size} unsaved row(s). Continue and discard them?`)) {
            e.target.value = '';
            return;
        }
        const fd = new FormData(); fd.append('file', file);
        try {
            const res = await axios.post(`${API_URL}/api/teacher-salary/payments/upload?month=${month}`, fd, { withCredentials: true });
            toast.success(`Updated ${res.data.updated} row(s)`);
            if (res.data.errors?.length) toast.error(`${res.data.errors.length} row(s) had errors`);
            reload();
        } catch (err) { toast.error('Upload failed'); }
        e.target.value = '';
    };

    return (
        <Card className="card-surface">
            <CardHeader className="flex flex-row items-center justify-between gap-3">
                <div>
                    <CardTitle className="font-['Outfit']">Payments</CardTitle>
                    {isAdmin && (
                        <p className="text-xs text-[#4B5563] mt-1">
                            Edits save automatically when you leave a cell. {dirtyIds.size > 0 && <span className="text-[#F59E0B] font-medium">{dirtyIds.size} unsaved change(s)</span>}
                        </p>
                    )}
                </div>
                <div className="flex gap-2 items-center">
                    <Select value={month} onValueChange={(v) => {
                        if (dirtyIds.size > 0 && !window.confirm(`You have ${dirtyIds.size} unsaved row(s). Switch months anyway?`)) return;
                        setMonth(v);
                    }}>
                        <SelectTrigger className="w-48"><SelectValue /></SelectTrigger>
                        <SelectContent>{currentSchoolYearMonths().map(m => <SelectItem key={m} value={m}>{new Date(m + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</SelectItem>)}</SelectContent>
                    </Select>
                    {isAdmin && <>
                        {dirtyIds.size > 0 && (
                            <Button onClick={saveAllDirty} className="bg-[#064E3B] hover:bg-[#047857]" data-testid="save-all-payments-btn">
                                Save all ({dirtyIds.size})
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => window.open(`${API_URL}/api/teacher-salary/payments/template`, '_blank')}>Template</Button>
                        <label className="inline-flex"><input type="file" accept=".xlsx" className="hidden" onChange={upload} /><span className="cursor-pointer inline-flex items-center px-3 py-1.5 border border-[#D1D5DB] rounded-md bg-white text-sm hover:bg-[#F3F4F6]"><Upload className="h-4 w-4 mr-1" />Upload Excel</span></label>
                    </>}
                </div>
            </CardHeader>
            <CardContent className="p-0">
                <Table>
                    <TableHeader><TableRow><TableHead>Teacher</TableHead><TableHead className="text-right">Hourly Rate</TableHead><TableHead className="text-right">Paid</TableHead></TableRow></TableHeader>
                    <TableBody>
                        {rows.map(r => {
                            const isDirty = dirtyIds.has(r.teacher_id);
                            return (
                                <TableRow key={r.teacher_id}>
                                    <TableCell>{r.teacher_name}</TableCell>
                                    <TableCell className="text-right tabular-nums">{formatEur(r.hourly_rate)}</TableCell>
                                    <TableCell className="text-right tabular-nums">
                                        {isAdmin ? (
                                            <div className="flex items-center justify-end gap-2">
                                                {savingId === r.teacher_id && <span className="text-xs text-[#6B7280]">Saving…</span>}
                                                {isDirty && savingId !== r.teacher_id && <span className="text-xs text-[#F59E0B]">●</span>}
                                                <Input
                                                    type="number"
                                                    step="0.01"
                                                    value={drafts[r.teacher_id] ?? ''}
                                                    onChange={(e) => onChange(r.teacher_id, e.target.value)}
                                                    onBlur={() => isDirty && saveOne(r.teacher_id)}
                                                    onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
                                                    className={`w-32 text-right ${isDirty ? 'border-[#F59E0B]' : ''}`}
                                                    data-testid={`payment-input-${r.teacher_id}`}
                                                />
                                            </div>
                                        ) : formatEur(r.amount)}
                                    </TableCell>
                                </TableRow>
                            );
                        })}
                    </TableBody>
                </Table>
            </CardContent>
        </Card>
    );
};

// ---------------- Tab 6: Pro Bono ----------------
const ProBonoTab = ({ teachers, programs, classes, isAdmin }) => {
    const [rows, setRows] = useState([]);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ teacher_id: '', program_id: '', start_date: '', end_date: '', note: '' });
    const reload = () => axios.get(`${API_URL}/api/teacher-salary/pro-bono`, { withCredentials: true }).then(r => setRows(r.data));
    useEffect(() => { reload(); }, []);

    // Programs the chosen teacher actually teaches
    const teacherPrograms = useMemo(() => {
        const pids = new Set(classes.filter(c => c.main_teacher_id === form.teacher_id).map(c => c.program_id));
        return programs.filter(p => pids.has(p.id));
    }, [classes, programs, form.teacher_id]);

    const submit = async () => {
        try { await axios.post(`${API_URL}/api/teacher-salary/pro-bono`, form, { withCredentials: true }); toast.success('Saved'); setOpen(false); reload(); }
        catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
    };
    const del = async (id) => { if (!window.confirm('Delete?')) return; await axios.delete(`${API_URL}/api/teacher-salary/pro-bono/${id}`, { withCredentials: true }); reload(); };

    return (
        <Card className="card-surface">
            <CardHeader className="flex flex-row items-center justify-between"><CardTitle className="font-['Outfit'] flex items-center gap-2"><Heart className="h-5 w-5 text-[#EF4444]" />Pro Bono</CardTitle>
                {isAdmin && <Button onClick={() => { setForm({ teacher_id: '', program_id: '', start_date: '', end_date: '', note: '' }); setOpen(true); }} className="bg-[#064E3B] hover:bg-[#047857]"><Plus className="h-4 w-4 mr-1" />Add Pro Bono</Button>}
            </CardHeader>
            <CardContent className="p-0">
                <Table>
                    <TableHeader><TableRow><TableHead>Teacher</TableHead><TableHead>Program</TableHead><TableHead>From</TableHead><TableHead>To</TableHead><TableHead>Note</TableHead>{isAdmin && <TableHead className="text-right">Actions</TableHead>}</TableRow></TableHeader>
                    <TableBody>
                        <YearGroups rows={rows} dateOf={r => r.start_date} colSpan={isAdmin ? 6 : 5} emptyText="No pro bono records for 2026-2027." render={r => <TableRow key={r.id}>
                            <TableCell>{r.teacher_name}</TableCell><TableCell>{r.program_short_name}</TableCell>
                            <TableCell className="tabular-nums">{r.start_date}</TableCell><TableCell className="tabular-nums">{r.end_date}</TableCell>
                            <TableCell className="text-xs">{r.note}</TableCell>
                            {isAdmin && <TableCell className="text-right"><Button size="sm" variant="outline" className="text-[#EF4444]" onClick={() => del(r.id)}><Trash2 className="h-3 w-3" /></Button></TableCell>}
                        </TableRow>} />
                    </TableBody>
                </Table>
            </CardContent>
            {isAdmin && <Dialog open={open} onOpenChange={setOpen}><DialogContent>
                <DialogHeader><DialogTitle className="font-['Outfit']">Add Pro Bono</DialogTitle></DialogHeader>
                <div className="space-y-3">
                    <div className="space-y-1.5"><Label>Teacher</Label>
                        <Select value={form.teacher_id} onValueChange={(v) => setForm({ ...form, teacher_id: v, program_id: '' })}>
                            <SelectTrigger><SelectValue placeholder="Pick teacher" /></SelectTrigger>
                            <SelectContent>{teachers.map(t => <SelectItem key={t.id} value={t.id}>{t.first_name} {t.last_name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="space-y-1.5"><Label>Program</Label>
                        <Select value={form.program_id} onValueChange={(v) => setForm({ ...form, program_id: v })}>
                            <SelectTrigger><SelectValue placeholder={form.teacher_id ? 'Pick program' : 'Pick teacher first'} /></SelectTrigger>
                            <SelectContent>{teacherPrograms.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                        </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1.5"><Label>From</Label><Input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
                        <div className="space-y-1.5"><Label>To</Label><Input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
                    </div>
                    <div className="space-y-1.5"><Label>Note</Label><Input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} /></div>
                </div>
                <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button onClick={submit} className="bg-[#064E3B] hover:bg-[#047857]">Save</Button></DialogFooter>
            </DialogContent></Dialog>}
        </Card>
    );
};

const Stat = ({ label, value, accent, positive }) => (
    <div className={`rounded-lg p-3 border ${accent ? 'bg-[#064E3B] border-[#064E3B] text-white' : 'bg-white border-[#E5E7EB]'}`}>
        <p className={`text-xs ${accent ? 'text-white/80' : 'text-[#4B5563]'}`}>{label}</p>
        <p className={`text-lg font-bold tabular-nums ${accent ? 'text-white' : (positive === false ? 'text-[#EF4444]' : 'text-[#1F2937]')}`}>{value}</p>
    </div>
);

export default Salaries;
